import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Box, Breakpoint, Stack, SxProps, Theme } from '@mui/material'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import Swiper from 'swiper'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import 'swiper/swiper-bundle.min.css'

import responsiveHook from '@hooks/useResponsive'
import windowSizeHook from '@hooks/useWindowSize'

import arrayUtils from '@utils/arrays'

import {
  arrowSx,
  itemsWrapperSx,
  leftArrowWrapperSx,
  rightArrowWrapperSx,
  swiperSx,
} from './styles'

export interface CarouselItem {
  image: {
    name: string
    src: string
  }
  link?: string
}

interface IBaseCarouselProps<T> {
  id: string
  items: T[]
  maxItemsPerSlide?: number
  whiteArrows?: boolean
  itemsWrapperSx?: SxProps<Theme>
  leftArrowWrapperSx?: SxProps<Theme>
  rightArrowWrapperSx?: SxProps<Theme>
  renderItem: (data: T, key: string) => ReactNode
  onItemsWrapperWidthChange?: (data: { width: number }) => void
}

type DynamicItems = {
  dynamic: true
  size: Partial<Record<Breakpoint, number>>
}

export type CarouselItemProps<T> = IBaseCarouselProps<T> & {
  dontCenterWhenFewerItems?: boolean
  numberOfItemsPerPage: number | DynamicItems
}

const Carousel = <T,>({
  id,
  items,
  maxItemsPerSlide,
  numberOfItemsPerPage,
  dontCenterWhenFewerItems = false,
  whiteArrows = false,
  leftArrowWrapperSx: leftArrowWrapperOverrideSx,
  rightArrowWrapperSx: rightArrowWrapperOverrideSx,
  itemsWrapperSx: itemsWrapperOverrideSx,
  renderItem,
  onItemsWrapperWidthChange,
}: CarouselItemProps<T>) => {
  const swiperRef = useRef<Swiper>()

  const { width } = windowSizeHook.useWindowSize()
  const { getNearestBreakpoint } = responsiveHook.useResponsive()

  const isDynamicNumberOfItems = useMemo(
    () => (numberOfItemsPerPage as DynamicItems).dynamic,
    [numberOfItemsPerPage]
  )

  const nearestBreakpoint = isDynamicNumberOfItems
    ? getNearestBreakpoint((numberOfItemsPerPage as DynamicItems)?.size)
    : undefined

  const [itemsState, setItemsState] = useState<T[][]>()

  const paginateCarouselItemsBasedOnWrapperWidth = useCallback(
    ({ widthInPx }: { widthInPx: number }) => {
      if (!nearestBreakpoint) {
        return
      }

      const itemWidth = (numberOfItemsPerPage as DynamicItems).size[
        nearestBreakpoint
      ]!

      let itemsPerPage = Math.floor(widthInPx / itemWidth)

      if (maxItemsPerSlide !== undefined) {
        itemsPerPage = Math.min(itemsPerPage, maxItemsPerSlide)
      }

      itemsPerPage = Math.max(itemsPerPage, 1)

      let arrayIndex = 1
      const finalArray: T[][] = [[]]

      items.forEach((v) => {
        if (arrayIndex > itemsPerPage) {
          finalArray.push([])
          arrayIndex = 1
        }

        arrayUtils.getArrayItemAt({ arr: finalArray, index: -1 })?.push(v)
        arrayIndex++
      })

      return finalArray
    },
    [nearestBreakpoint, numberOfItemsPerPage, maxItemsPerSlide, items]
  )

  const refreshItems = useCallback(
    ({ widthInPx }: { widthInPx: number }) => {
      if (typeof numberOfItemsPerPage === 'number') {
        setItemsState(
          items
            .map((v, index) =>
              items.slice(
                index * numberOfItemsPerPage,
                index * numberOfItemsPerPage + numberOfItemsPerPage
              )
            )
            .filter((v) => v.length > 0)
        )
      } else {
        setItemsState(paginateCarouselItemsBasedOnWrapperWidth({ widthInPx }))
      }
    },
    [items, numberOfItemsPerPage, paginateCarouselItemsBasedOnWrapperWidth]
  )

  useEffect(() => {
    refreshItems?.({
      widthInPx: window.innerWidth,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isDynamicNumberOfItems) {
      return
    }

    const swiperWrapperWidthInPx = document
      .querySelector('#items-wrapper')
      ?.getBoundingClientRect().width

    if (swiperWrapperWidthInPx) {
      refreshItems?.({
        widthInPx: swiperWrapperWidthInPx,
      })
    }
  }, [width, isDynamicNumberOfItems, onItemsWrapperWidthChange, refreshItems])

  useEffect(() => {
    swiperRef.current = new Swiper(`.${id}`, {
      autoplay: {
        delay: 5_000,
        disableOnInteraction: false,
      },
      loop: true,
      modules: [Navigation, Pagination, Autoplay],
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
    })
  }, [id])

  useEffect(() => {
    swiperRef.current?.slideTo(0)
    swiperRef.current?.update()
  }, [width, itemsState])

  const isLastPageWithFewerItems = (itemsLength: number) =>
    itemsLength < itemsState![0].length

  return (
    <Box className={`${id} swiper`} sx={swiperSx}>
      <Box className="swiper-wrapper">
        {itemsState?.map((page, pageIndex) => (
          <Box key={pageIndex} className="swiper-slide">
            <Stack
              id="items-wrapper"
              direction="row"
              sx={
                {
                  ...itemsWrapperSx({
                    dontCenterWhenFewerItems:
                      dontCenterWhenFewerItems &&
                      isLastPageWithFewerItems(page.length),
                  }),
                  ...itemsWrapperOverrideSx,
                } as SxProps<Theme>
              }
            >
              {page.map((v, itemIndex) =>
                renderItem(v, `${pageIndex}-${itemIndex}`)
              )}
            </Stack>
          </Box>
        ))}
      </Box>

      <Box className="swiper-pagination" />

      <Box
        className="swiper-button-prev"
        sx={
          {
            ...leftArrowWrapperSx,
            ...leftArrowWrapperOverrideSx,
          } as SxProps<Theme>
        }
      >
        <ChevronLeftIcon sx={arrowSx({ white: whiteArrows })} />
      </Box>

      <Box
        className="swiper-button-next"
        sx={
          {
            ...rightArrowWrapperSx,
            ...rightArrowWrapperOverrideSx,
          } as SxProps<Theme>
        }
      >
        <ChevronRightIcon sx={arrowSx({ white: whiteArrows })} />
      </Box>
    </Box>
  )
}

export { Carousel }
